// `BusyBee` — the four-frame flip-book sprite that stands for a live Beekeeper turn
// (`beekeeper-progress-indicator` §4; sketch `busy-bee.html`). One component, three call sites:
// `ChatOverlay`'s header (`submitting`/`running`, and a parked frame for `uncertain`), the no-box
// "Thinking" placeholder in the transcript (`thinking-placeholder.html`, variant **C** — decided
// 2026-08-10), and `ChatReentryRow`'s busy treatment (`reentry-row-states.html`).
//
// **Mechanics, exactly as the sketch proved.** The strip
// (`client/public/sprites/ui/bee_busy_strip.svg`, 64×16, four 16×16 frames — wing high/mid/low/mid)
// is a `background-image` at `background-size: 400% 100%`; the flip-book steps across it by
// animating `background-position` from `0% 0` to `100% 0` with `steps(4)` easing over 480ms,
// infinite. This runs over **WAAPI** (`element.animate()` in a `useEffect`) rather than a
// stylesheet keyframe — the same pattern `ChatReentryRow`'s (now-removed) pulse and
// `PlaybackBar`'s spinner already established — guarded by `typeof element.animate === 'function'`
// (absent under jsdom) and by `prefers-reduced-motion`, and cancelled on unmount.
//
// **`animated={false}` is not the same thing as reduced motion.** It parks the strip on frame 1
// (wing mid) unconditionally, independent of the media query — used for the `uncertain` phase,
// where the honest picture is a still bee: nothing is verifiably in motion, so animating one would
// be a claim the client cannot back up. Reduced motion is a *rendering* constraint on top of
// whatever `animated` says; forcing `animated={false}` is a *semantic* one.
//
// **Box size must be `16 × n` only** (×2 = 32px, ×3 = 48px). A non-integer scale (×1.5 = 24px)
// bleeds the neighbouring frame into the box — found the hard way in the sketch's own first render.
//
// Decoration only: `aria-hidden` always. The header's status line is what a screen reader hears.

import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';

const STRIP_URL = '/sprites/ui/bee_busy_strip.svg';

/** `16 × n` only — see the file header's sizing note. */
export type BusyBeeSize = 16 | 32 | 48;

export interface BusyBeeProps {
  /**
   * Whether this instance should attempt to flap at all. `false` parks it on frame 1 (wing mid)
   * unconditionally — not an accessibility fallback, a statement that nothing is in motion (the
   * `uncertain` phase's own case). `true` attempts to animate, itself still subject to the
   * `prefers-reduced-motion`/jsdom guards below.
   */
  animated: boolean;
  /** Box size in px, `16 × n` only. Defaults to 16 (`ChatReentryRow`'s size). */
  size?: BusyBeeSize;
  style?: CSSProperties;
}

/** Frame index 1 of 4 (wing mid) — `i / (frameCount - 1) * 100%` for `i = 1`, the same formula
 * `background-size: 400% 100%` resolves `background-position` percentages against. The most
 * legible single frame, and where the sketch's own `prefers-reduced-motion` rule parks. */
const PARKED_BACKGROUND_POSITION = '33.333% 0';
const FLAP_DURATION_MS = 480;

export function BusyBee({ animated, size = 16, style }: BusyBeeProps) {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!animated || element === null || typeof element.animate !== 'function') {
      return undefined;
    }
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }
    const animation = element.animate(
      [{ backgroundPosition: '0% 0' }, { backgroundPosition: '100% 0' }],
      { duration: FLAP_DURATION_MS, iterations: Infinity, easing: 'steps(4)' },
    );
    return () => animation.cancel();
  }, [animated]);

  return (
    <span
      ref={ref}
      aria-hidden="true"
      data-testid="busy-bee"
      style={{
        display: 'inline-block',
        flex: 'none',
        width: size,
        height: size,
        backgroundImage: `url(${STRIP_URL})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '400% 100%',
        backgroundPosition: PARKED_BACKGROUND_POSITION,
        imageRendering: 'pixelated',
        ...style,
      }}
    />
  );
}
